/**
 * Which requests the contract requires an `Idempotency-Key` on.
 *
 * Invariant: `requiresIdempotencyKey(method, path)` is true exactly for a
 * concrete request to an operation listed in `generated/idempotency.ts`
 * (regenerated from `contracts/openapi.yaml`). Clients never mint a key for
 * any other request: an optional key only means something when it is reused
 * across the retries of one user action, which only the caller can know.
 */
import { IDEMPOTENCY_KEY_REQUIRED } from './generated/idempotency';

/** `{param}` matches one path segment, never a `/` or the `:` of an action. */
function templateToPattern(template: string): RegExp {
  const source = template
    .split(/(\{[^}]+\})/)
    .map((part) => (part.startsWith('{') ? '[^/:]+' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
    .join('');
  return new RegExp(`^${source}$`);
}

const REQUIRED: ReadonlyArray<readonly [string, RegExp]> = IDEMPOTENCY_KEY_REQUIRED.map(
  ([method, template]) => [method, templateToPattern(template)] as const,
);

/**
 * Whether the contract requires `Idempotency-Key` on `method path`. `path`
 * is the concrete request path (`/api/v1/workspaces/<id>/pause`); a query
 * string or an origin prefix is ignored.
 */
export function requiresIdempotencyKey(method: string, path: string): boolean {
  let pathname = path;
  try {
    pathname = new URL(path, 'http://contract.invalid').pathname;
  } catch {
    /* not a URL: match it as given */
  }
  const verb = method.toUpperCase();
  return REQUIRED.some(([m, pattern]) => m === verb && pattern.test(pathname));
}
